import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Card, Row, Col, Badge, Spinner, ProgressBar, Tab, Tabs } from 'react-bootstrap';
import { FaIdCard, FaFileUpload, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaUser, FaCamera, FaHome, FaCreditCard } from 'react-icons/fa';
import api from '../services/api';

const KycUploadModal = ({ show, onHide, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('pan');
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [kycStatus, setKycStatus] = useState({});
  const [documents, setDocuments] = useState([]);

  // Document types configuration
  const documentTypes = [
    {
      key: 'pan',
      label: 'PAN Card',
      icon: <FaCreditCard className="text-primary" />,
      description: 'Upload your PAN card for identity verification',
      required: true,
      acceptedTypes: '.jpg,.jpeg,.png,.pdf',
      maxSize: '2MB'
    },
    {
      key: 'aadhar',
      label: 'Aadhar Card',
      icon: <FaIdCard className="text-success" />,
      description: 'Upload your Aadhar card for address verification',
      required: true,
      acceptedTypes: '.jpg,.jpeg,.png,.pdf',
      maxSize: '2MB'
    },
    {
      key: 'address_proof',
      label: 'Address Proof',
      icon: <FaHome className="text-info" />,
      description: 'Upload utility bill, bank statement, or rental agreement',
      required: false,
      acceptedTypes: '.jpg,.jpeg,.png,.pdf',
      maxSize: '2MB'
    },
    {
      key: 'photo',
      label: 'Photograph',
      icon: <FaCamera className="text-warning" />,
      description: 'Upload your recent passport size photograph',
      required: false,
      acceptedTypes: '.jpg,.jpeg,.png',
      maxSize: '2MB'
    }
  ];

  // Form states for each document type
  const [forms, setForms] = useState({
    pan: { file: null, document_number: '', dob: '' },
    aadhar: { file: null, document_number: '', name: '' },
    address_proof: { file: null, document_number: '' },
    photo: { file: null, document_number: '' }
  });

  useEffect(() => {
    if (show) {
      fetchKycStatus();
      fetchDocuments();
    }
  }, [show]);

  const fetchKycStatus = async () => {
    try {
      const response = await api.get('/api/kyc/status');
      setKycStatus(response.data.data);
    } catch (error) {
      console.error('Error fetching KYC status:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/api/kyc/documents');
      setDocuments(response.data.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  // PAN validation function
  const validatePAN = (pan) => {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panRegex.test(pan);
  };

  // Format PAN input (uppercase, remove spaces, allow both letters and numbers)
  const formatPAN = (value) => {
    // Remove any characters that are not letters or numbers, convert to uppercase
    return value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 10);
  };

  // Aadhaar validation function
  const validateAadhaar = (aadhaar) => {
    const aadhaarRegex = /^[0-9]{12}$/;
    return aadhaarRegex.test(aadhaar);
  };

  // Format Aadhaar input (numbers only)
  const formatAadhaar = (value) => {
    return value.replace(/[^0-9]/g, '').slice(0, 12);
  };

  const handleFileChange = (docType, file) => {
    if (file && file.size > 2 * 1024 * 1024) { // 2MB limit
      setMessage({ type: 'danger', text: 'File size should not exceed 2MB' });
      return;
    }

    setForms(prev => ({
      ...prev,
      [docType]: {
        ...prev[docType],
        file: file
      }
    }));

    setMessage({ type: 'success', text: `${docType.toUpperCase()} file selected successfully` });
  };

  const handleDocumentNumberChange = (docType, value) => {
    let formattedValue = value;
    let isValid = true;
    let errorMessage = '';

    if (docType === 'pan') {
      // Format PAN: uppercase, alphanumeric only, max 10 chars
      formattedValue = formatPAN(value);
      
      if (formattedValue.length === 10) {
        isValid = validatePAN(formattedValue);
        if (!isValid) {
          errorMessage = 'Invalid PAN format. Should be: ABCDE1234F (5 letters + 4 numbers + 1 letter)';
        }
      } else if (formattedValue.length > 0) {
        errorMessage = `PAN should be 10 characters (${formattedValue.length}/10)`;
      }
    } else if (docType === 'aadhar') {
      // Format Aadhaar: numbers only, max 12 digits
      formattedValue = formatAadhaar(value);
      
      if (formattedValue.length === 12) {
        isValid = validateAadhaar(formattedValue);
        if (!isValid) {
          errorMessage = 'Invalid Aadhaar format. Should be 12 digits only';
        }
      } else if (formattedValue.length > 0) {
        errorMessage = `Aadhaar should be 12 digits (${formattedValue.length}/12)`;
      }
    }

    setForms(prev => ({
      ...prev,
      [docType]: {
        ...prev[docType],
        document_number: formattedValue,
        isValid: isValid,
        errorMessage: errorMessage
      }
    }));

    // Show validation message
    if (errorMessage) {
      setMessage({ type: 'warning', text: errorMessage });
    } else if (formattedValue.length > 0 && isValid) {
      setMessage({ type: 'success', text: `${docType.toUpperCase()} number format is valid` });
    } else {
      setMessage({ type: '', text: '' });
    }
  };

  // PAN Verification function (before upload)
  const handlePANVerification = async (docType) => {
    const formData = forms[docType];
    
    if (docType !== 'pan') {
      setMessage({ type: 'danger', text: 'PAN verification only available for PAN documents' });
      return;
    }

    if (!formData.document_number || !validatePAN(formData.document_number)) {
      setMessage({ type: 'danger', text: 'Please enter a valid PAN number (e.g., ABCDE1234F)' });
      return;
    }

    if (!formData.dob) {
      setMessage({ type: 'danger', text: 'Please enter date of birth for PAN verification' });
      return;
    }

    setUploading(true);
    setMessage({ type: 'info', text: 'Verifying PAN with PaySprint...' });

    try {
      const response = await api.post('/api/kyc/pan-verify', {
        pan: formData.document_number,
        dob: formData.dob
      });

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: `PAN verification successful! ${response.data.data.note || 'Ready for document upload'}` 
        });
        
        // Mark PAN as verified in form
        setForms(prev => ({
          ...prev,
          [docType]: {
            ...prev[docType],
            verified: true,
            verificationData: response.data.data
          }
        }));
      } else {
        setMessage({ 
          type: 'danger', 
          text: response.data.message || 'PAN verification failed' 
        });
      }
    } catch (error) {
      console.error('PAN verification error:', error);
      setMessage({ 
        type: 'danger', 
        text: error.response?.data?.message || 'PAN verification service error' 
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async (docType) => {
    const formData = forms[docType];
    
    if (!formData.file) {
      setMessage({ type: 'danger', text: 'Please select a file to upload' });
      return;
    }

    setUploading(true);
    setMessage({ type: 'info', text: `Uploading ${docType.toUpperCase()} document...` });

    try {
      const uploadData = new FormData();
      uploadData.append('document_type', docType);
      uploadData.append('document_number', formData.document_number);
      uploadData.append('file', formData.file);

      const response = await api.post('/api/kyc/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setMessage({ 
        type: 'success', 
        text: `${docType.toUpperCase()} document uploaded successfully!` 
      });

      // Reset form
      setForms(prev => ({
        ...prev,
        [docType]: { file: null, document_number: '' }
      }));

      // Refresh data
      await fetchKycStatus();
      await fetchDocuments();

      if (onSuccess) {
        onSuccess(response.data.data);
      }

    } catch (error) {
      setMessage({ 
        type: 'danger', 
        text: `Upload failed: ${error.response?.data?.message || error.message}` 
      });
    } finally {
      setUploading(false);
    }
  };

  const getDocumentStatus = (docType) => {
    const doc = documents.find(d => d.document_type === docType);
    if (!doc) return { status: 'not_uploaded', badge: 'secondary' };

    const statusMap = {
      'approved': { status: 'Approved', badge: 'success' },
      'rejected': { status: 'Rejected', badge: 'danger' },
      'pending': { status: 'Pending', badge: 'warning' },
      'under_review': { status: 'Under Review', badge: 'info' }
    };

    return statusMap[doc.status] || { status: 'Unknown', badge: 'secondary' };
  };

  const getOverallStatus = () => {
    if (kycStatus.overall_status === 'completed') {
      return { status: 'KYC Completed', badge: 'success', icon: <FaCheckCircle /> };
    } else if (kycStatus.overall_status === 'rejected') {
      return { status: 'KYC Rejected', badge: 'danger', icon: <FaTimesCircle /> };
    } else if (kycStatus.overall_status === 'pending') {
      return { status: 'KYC Pending', badge: 'warning', icon: <FaExclamationTriangle /> };
    } else {
      return { status: 'KYC Incomplete', badge: 'secondary', icon: <FaExclamationTriangle /> };
    }
  };

  const renderDocumentForm = (docType) => {
    const docConfig = documentTypes.find(d => d.key === docType);
    const docStatus = getDocumentStatus(docType);
    const form = forms[docType];

    return (
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            {docConfig.icon}
            <span className="ms-2 fw-bold">{docConfig.label}</span>
            {docConfig.required && <Badge bg="danger" className="ms-2">Required</Badge>}
          </div>
          <Badge bg={docStatus.badge}>{docStatus.status}</Badge>
        </Card.Header>
        <Card.Body>
          <p className="text-muted">{docConfig.description}</p>
          
          {docStatus.status === 'Approved' ? (
            <Alert variant="success" className="mb-3">
              <FaCheckCircle className="me-2" />
              This document has been approved and verified.
            </Alert>
          ) : docStatus.status === 'Rejected' ? (
            <Alert variant="danger" className="mb-3">
              <FaTimesCircle className="me-2" />
              This document was rejected. Please upload a new one.
            </Alert>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>
                  Document Number {docType === 'pan' ? '(Required for verification)' : '(Optional)'}
                </Form.Label>
                <Form.Control
                  type="text"
                  inputMode={docType === 'pan' ? 'text' : docType === 'aadhar' ? 'numeric' : 'text'}
                  placeholder={
                    docType === 'pan' ? 'Enter PAN (e.g., ABCDE1234F)' :
                    docType === 'aadhar' ? 'Enter 12-digit Aadhaar number' :
                    `Enter ${docConfig.label} number`
                  }
                  value={form.document_number}
                  onChange={(e) => {
                    // Allow all input, let formatPAN handle filtering
                    handleDocumentNumberChange(docType, e.target.value);
                  }}
                  className={form.errorMessage ? 'is-invalid' : (form.isValid && form.document_number ? 'is-valid' : '')}
                  style={{ textTransform: docType === 'pan' ? 'uppercase' : 'none' }}
                  autoComplete="off"
                  maxLength={docType === 'pan' ? 10 : docType === 'aadhar' ? 12 : undefined}
                />
                {form.errorMessage && (
                  <div className="invalid-feedback">
                    {form.errorMessage}
                  </div>
                )}
                {form.isValid && form.document_number && !form.errorMessage && (
                  <div className="valid-feedback">
                    {docType.toUpperCase()} format is valid ✓
                  </div>
                )}
              </Form.Group>

              {/* Date of Birth for PAN verification */}
              {docType === 'pan' && (
                <Form.Group className="mb-3">
                  <Form.Label>Date of Birth (Required for PAN verification)</Form.Label>
                  <Form.Control
                    type="date"
                    value={form.dob || ''}
                    onChange={(e) => setForms(prev => ({
                      ...prev,
                      [docType]: { ...prev[docType], dob: e.target.value }
                    }))}
                    max={new Date().toISOString().split('T')[0]} // Max date is today
                  />
                  <Form.Text className="text-muted">
                    Date of birth as per PAN card
                  </Form.Text>
                </Form.Group>
              )}

              {/* Name for Aadhaar verification */}
              {docType === 'aadhar' && (
                <Form.Group className="mb-3">
                  <Form.Label>Full Name (As per Aadhaar)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter full name as per Aadhaar card"
                    value={form.name || ''}
                    onChange={(e) => setForms(prev => ({
                      ...prev,
                      [docType]: { ...prev[docType], name: e.target.value }
                    }))}
                  />
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Upload File</Form.Label>
                <Form.Control
                  type="file"
                  accept={docConfig.acceptedTypes}
                  onChange={(e) => handleFileChange(docType, e.target.files[0])}
                  disabled={uploading}
                />
                <Form.Text className="text-muted">
                  Accepted formats: {docConfig.acceptedTypes} (Max size: {docConfig.maxSize})
                </Form.Text>
              </Form.Group>

              {form.file && (
                <div className="mb-3">
                  <small className="text-success">
                    Selected: {form.file.name} ({(form.file.size / 1024 / 1024).toFixed(2)} MB)
                  </small>
                </div>
              )}

              {/* PAN Verification Button */}
              {docType === 'pan' && !form.verified && (
                <Button
                  variant="info"
                  onClick={() => handlePANVerification(docType)}
                  disabled={!form.document_number || !form.dob || !validatePAN(form.document_number) || uploading}
                  className="w-100 mb-2"
                >
                  {uploading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Verifying PAN...
                    </>
                  ) : (
                    <>
                      <FaCheckCircle className="me-2" />
                      Verify PAN with PaySprint
                    </>
                  )}
                </Button>
              )}

              {/* Upload Button */}
              <Button
                variant="primary"
                onClick={() => handleUpload(docType)}
                disabled={!form.file || uploading}
                className="w-100"
              >
                {uploading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FaFileUpload className="me-2" />
                    Upload {docConfig.label}
                  </>
                )}
              </Button>
            </>
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaIdCard className="me-2" />
          KYC Document Upload
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {message.text && (
          <Alert variant={message.type} dismissible onClose={() => setMessage({ type: '', text: '' })}>
            {message.text}
          </Alert>
        )}

        {/* Overall KYC Status */}
        <Card className="mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1">Overall KYC Status</h5>
                <p className="text-muted mb-0">Complete your KYC verification to access all services</p>
              </div>
              <Badge bg={getOverallStatus().badge} className="fs-6">
                {getOverallStatus().icon} {getOverallStatus().status}
              </Badge>
            </div>
          </Card.Body>
        </Card>

        {/* Document Upload Tabs */}
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          {documentTypes.map((docType) => (
            <Tab
              key={docType.key}
              eventKey={docType.key}
              title={
                <div className="d-flex align-items-center">
                  {docType.icon}
                  <span className="ms-1">{docType.label}</span>
                  {getDocumentStatus(docType.key).status === 'Approved' && (
                    <FaCheckCircle className="ms-1 text-success" />
                  )}
                </div>
              }
            >
              {renderDocumentForm(docType.key)}
            </Tab>
          ))}
        </Tabs>

        {/* Uploaded Documents Summary */}
        {documents.length > 0 && (
          <Card>
            <Card.Header>
              <h6 className="mb-0">Uploaded Documents</h6>
            </Card.Header>
            <Card.Body>
              <Row>
                {documents.map((doc) => (
                  <Col md={6} key={doc.id} className="mb-2">
                    <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                      <div>
                        <small className="fw-bold text-capitalize">{doc.document_type}</small>
                        <br />
                        <small className="text-muted">{doc.file_name}</small>
                      </div>
                      <Badge bg={getDocumentStatus(doc.document_type).badge}>
                        {getDocumentStatus(doc.document_type).status}
                      </Badge>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default KycUploadModal;
